import { FC, useContext, useEffect, useRef, useState } from 'react';
import ScrollAnimation from 'react-animate-on-scroll';
import styled, { css } from 'styled-components';

import ContainerSmall from '@/components/ContainerSmall/ContainerSmall';
import { getImage } from '@/lib/fetchAPI';
import { GlobalContext } from '@/pages/_app';

const VideoSection: FC<any> = ({ data }) => {
	const { lang } = useContext(GlobalContext);
	const videoNode = useRef<any>(null);
	const videoPrewiewNode = useRef<any>(null);
	const [videoStatus, changeVideoStatus] = useState(false);

	const playVideo = () => {
		videoNode.current.play();
		changeVideoStatus(true);
	};

	const collapsVideo = () => {
		changeVideoStatus(false);
	};

	useEffect(() => {
		videoNode.current?.addEventListener('ended', collapsVideo);

		return () => videoNode.current?.removeEventListener('ended', collapsVideo);
	}, [videoNode.current]);

	return (
		<Section>
			<ContainerSmall>
				<ScrollAnimation animateOnce animateIn="fadeInUp" duration={0.8}>
					<Content $active={videoStatus}>
						<VideoPreview $active={videoStatus}>
							<video
								ref={videoPrewiewNode}
								loop
								muted
								autoPlay
								playsInline
								src={getImage(data?.video)}
							/>
						</VideoPreview>

						<VideoWrapper $active={videoStatus}>
							<Video
								ref={videoNode}
								muted
								controls
								src={'/videos/marketing-video-' + lang + '.mp4'}
							/>
						</VideoWrapper>

						<Background $active={videoStatus} />

						<PreviewContent $active={videoStatus}>
							<Title>{data?.title}</Title>

							<Button onClick={() => playVideo()}>
								<Icon alt="play" src="/images/icons/video-section/play.svg" />
							</Button>
						</PreviewContent>

						<picture>
							<VideoShadow src="/images/gradients/video.webp" />
						</picture>
					</Content>
				</ScrollAnimation>
			</ContainerSmall>
		</Section>
	);
};

const VideoShadow = styled.img`
	position: absolute;
	top: 130px;
	left: -60px;
	max-width: 100%;
	width: 100%;
	z-index: -1;

	@media (max-width: 600px) {
		bottom: -130px;
		top: auto;
	}
`;

const Section = styled.section`
	margin: 90px 0;
	padding: 0 15px;

	@media (max-width: 600px) {
		margin: 40px 0 45px;
	}
`;

const Background = styled.div<{ $active: boolean }>`
	position: absolute;
	width: 100%;
	height: 100%;
	top: 0;
	left: 0;
	background-color: var(--color-purple);
	opacity: 0.76;
	border-radius: 70px;
	z-index: 200;
	visibility: visible;
	transition: 0.3s;

	@media (max-width: 600px) {
		border-radius: 40px;
	}

	${({ $active }) =>
		$active &&
		css`
			visibility: hidden;
			opacity: 0;
		`};
`;

const VideoWrapper = styled.div<{ $active: boolean }>`
	position: absolute;
	width: 100%;
	height: 100%;
	top: 0;
	left: 0;
	border-radius: 70px;
	transition: 0.13s border-radius 0.05s;
	mask-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAA5JREFUeNpiYGBgAAgwAAAEAAGbA+oJAAAAAElFTkSuQmCC);
	z-index: 90;

	${({ $active }) =>
		$active &&
		css`
			border-radius: 0;
			mask-image: none;
		`}
`;

const VideoPreview = styled.div<{ $active: boolean }>`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	border-radius: 70px;
	z-index: 100;
	mask-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAA5JREFUeNpiYGBgAAgwAAAEAAGbA+oJAAAAAElFTkSuQmCC);
	visibility: visible;
	opacity: 1;
	transition: 0.3s;

	@media (max-width: 600px) {
		border-radius: 40px;
	}
	video {
		position: absolute;
		width: 100%;
		height: 100%;
		z-index: 100;
		top: 0;
		left: 0;
	}

	${({ $active }) =>
		$active &&
		css`
			visibility: hidden;
			opacity: 0;
		`};
`;

const Content = styled.div<{ $active: boolean }>`
	position: relative;
	transition: 0.5s;
	width: 100%;
	padding-top: 56.3%;

	&:after,
	&:before {
		content: '';
		position: absolute;
		background-color: var(--color-purple-60);
		width: 100%;
		height: 70px;
		left: 0;
		z-index: 80;
		transition: 0.3s;
		opacity: 1;

		@media (max-width: 600px) {
			display: none;
		}
	}

	&:after {
		top: 0;
		left: 0;
		border-top-left-radius: 70px;
		border-top-right-radius: 70px;
	}

	&:before {
		bottom: 0;
		left: 0;
		border-bottom-left-radius: 70px;
		border-bottom-right-radius: 70px;
	}

	@media (max-width: 600px) {
		border-radius: 30px;
		mask-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAA5JREFUeNpiYGBgAAgwAAAEAAGbA+oJAAAAAElFTkSuQmCC);
	}

	${({ $active }) =>
		$active &&
		css`
			&:before {
				bottom: -65px !important;
				opacity: 1;
			}

			&:after {
				top: -65px !important;
				opacity: 1;
			}
		`};
`;

const Video = styled.video`
	position: absolute;
	width: 100%;
	height: auto;
	top: 0;
	left: 0;
	outline: none;
`;

const PreviewContent = styled.div<{ $active: boolean }>`
	position: absolute;
	width: 100%;
	height: 100%;
	top: 0;
	left: 0;
	z-index: 300;
	visibility: visible;
	opacity: 1;
	transition: 0.3s;

	${({ $active }) =>
		$active &&
		css`
			visibility: hidden;
			opacity: 0;
		`}
`;

const Title = styled.h2`
	position: absolute;
	font-family: var(--font-sans);

	font-weight: var(--font-bold);
	font-size: 86px;
	line-height: 90px;
	text-align: center;
	color: var(--color-white);
	top: 21%;
	width: 100%;

	@media (max-width: 1000px) {
		font-size: 78px;
	}

	@media (max-width: 600px) {
		top: 22px;
		font-size: 40px;
		line-height: 40px;
	}
`;

const Button = styled.div`
	position: absolute;
	width: 120px;
	height: 120px;
	background-color: var(--color-white);
	border-radius: 45px;
	top: 45%;
	left: 50%;
	transform: translateX(-50%);
	cursor: pointer;
	transition: 0.3s;

	&:hover {
		border-radius: 60px;
	}

	@media (max-width: 1000px) {
		width: 100px;
		height: 100px;
		border-radius: 34px;
	}

	@media (max-width: 600px) {
		top: 80px;
		width: 80px;
		height: 80px;
		border-radius: 27px;
	}

	@media (max-width: 400px) {
		top: 78px;
		width: 65px;
		height: 65px;
		border-radius: 20px;
	}
`;

const Icon = styled.img`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
`;

export default VideoSection;
